import { useState, useRef, useEffect } from 'react';
import { BsPlus, BsThreeDotsVertical } from 'react-icons/bs';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Button } from '../../../../ui/button';
import DropdownButton from '../../../../components/Tools/DropdownButton';

const ChatSidebar = ({
    sidebarOpen,
    chatHistory,
    currentChatId,
    editingChatId,
    editingChatTitle,
    setEditingChatTitle,
    gptLimitReached,
    loading,
    isVideoAnalyzing,
    onNewChat,
    onSelectChat,
    onEditConversation,
    onSaveConversationTitle,
    onCancelEdit,
    onDeleteConversation,
    onDeleteAllConversations,
    renderError,
    renderEmptyState
}) => {
    const editInputRef = useRef(null);

    // Focus input when editing
    useEffect(() => {
        if (editingChatId && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingChatId]);

    // Handle click outside when editing title
    const handleClickOutside = (e) => {
        if (editInputRef.current && !editInputRef.current.contains(e.target)) {
            onCancelEdit();
        }
    };

    // Attach event listener when editing
    useEffect(() => {
        if (editingChatId) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editingChatId]);

    return (
        <div className={`${sidebarOpen ? 'w-full md:w-64' : 'w-0'} ${sidebarOpen ? 'h-max md:h-full' : 'h-0 md:h-full'} --theme-colors rounded-md border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out overflow-hidden absolute md:relative z-10`}>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <Button 
                    size="lg"
                    onClick={onNewChat}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                    disabled={gptLimitReached || loading || isVideoAnalyzing}
                >
                    <BsPlus className="h-5 w-5" />
                    New Chat
                </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
                {renderError()}
                {renderEmptyState()}
                
                {chatHistory.length > 0 && (
                    <div className="py-2">
                        <div className="px-4 py-2 flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Recent</span>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={onDeleteAllConversations}
                                className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1"
                                title="Delete all conversations"
                            >
                                <FiTrash2 className="h-3 w-3 mr-1" />
                                Clear All
                            </Button>
                        </div>
                        {chatHistory.map((chat) => (
                            <div 
                                key={chat.id}
                                className={`flex items-center justify-between px-4 py-2 ${
                                    currentChatId === chat.id 
                                        ? "border border-gray-500 rounded-md text-blue-600" 
                                        : "hover:bg-gray-300"
                                }`}
                            >
                                {editingChatId === chat.id ? (
                                    <form onSubmit={onSaveConversationTitle} className="flex-1 mr-2 flex items-center">
                                        <input
                                            ref={editInputRef}
                                            type="text"
                                            value={editingChatTitle}
                                            onChange={(e) => setEditingChatTitle(e.target.value)}
                                            className="w-full border bg-transparent border-gray-300 rounded-l px-2 py-1 text-sm focus:outline-none focus:ring-0 focus:border-gray-300"
                                        />
                                        <div>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                type="button"
                                                onClick={onSaveConversationTitle}
                                            >
                                                +
                                            </Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div
                                        onClick={() => onSelectChat(chat.id)}
                                        className="text-left truncate flex-1 text-sm l-2 cursor-pointer"
                                    >
                                        {chat.title || chat.preview || "New conversation"}
                                    </div>
                                )}
                                
                                {!editingChatId && (
                                    <DropdownButton
                                        label={<BsThreeDotsVertical className="h-4 w-4 text-gray-500" />}
                                        options={[
                                            {
                                                label: "Edit Name",
                                                icon: <FiEdit2 className="h-4 w-4" />,
                                                onClick: () => onEditConversation(chat)
                                            },
                                            {
                                                label: "Delete",
                                                icon: <FiTrash2 className="h-4 w-4 text-red-500" />,
                                                onClick: () => onDeleteConversation(chat.id),
                                                color: "rgb(239 68 68)" // text-red-500
                                            }
                                        ]}
                                        classes="p-1 rounded-full hover:bg-gray-200"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatSidebar; 